import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, surveyApi } from '@/api/client';
import { MOCK_ACTIONS } from '@/data/mockCatalog';
import type { ActionItem } from '@/api/types';

export function useActions(fundamentalId?: string) {
  return useQuery<ActionItem[]>({
    queryKey: ['actions', fundamentalId ?? 'all'],
    queryFn: async () => {
      if (USE_MOCK) {
        const actions = MOCK_ACTIONS as unknown as ActionItem[];
        return fundamentalId ? actions.filter(a => a.fundamentalId === fundamentalId) : actions;
      }
      return surveyApi.getActions(fundamentalId);
    },
  });
}

export function useCreateAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<ActionItem, 'id'>) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] createAction', body);
        return Promise.resolve({ ...body, id: crypto.randomUUID() } as ActionItem);
      }
      return surveyApi.createAction(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['actions'] }); },
  });
}

export function useUpdateAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ActionItem) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateAction', id, body);
        return Promise.resolve({ id, ...body } as ActionItem);
      }
      return surveyApi.updateAction(id, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['actions'] }); },
  });
}

export function useDeleteAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] deleteAction', id);
        return Promise.resolve();
      }
      return surveyApi.deleteAction(id);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['actions'] }); },
  });
}
